import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import PermissionRoute from '../../components/routing/PermissionRoute';
import { useAuth } from '../../hooks/useAuth';
import CSVReceivedPage from './CSVReceivedPage';
import CSVPagesPage from './CSVPagesPage';
import CSVUploadsPage from './CSVUploadsPage';
import EligibilityUploadsPage from './EligibilityUploadsPage';
import styles from './DataImportsPage.module.css';

/**
 * DataImportsPage - Hub that composes the four existing import pages into one
 * tabbed workspace. Each tab renders its ORIGINAL component by import; none of
 * them is rewritten or merged, so they stay independently routable.
 *
 * Each tab keeps the exact permission its standalone route carried in App.jsx,
 * and it keeps it through the SAME <PermissionRoute> gate rather than a second
 * hand-rolled copy of the check.
 */

const TABS = [
  { key: 'received', label: 'Received', permission: 'csv_received.view', Component: CSVReceivedPage },
  { key: 'upload-portals', label: 'Upload Portals', permission: 'csv_pages.view', Component: CSVPagesPage },
  { key: 'upload-history', label: 'Upload History', permission: 'csv_uploads.view', Component: CSVUploadsPage },
  { key: 'eligibility', label: 'Eligibility', permission: 'eligibility.view', Component: EligibilityUploadsPage }
];

const findTab = (key) => TABS.find((tab) => tab.key === key);

/**
 * A `?tab=` value selects a tab whenever it NAMES one — including a tab the role
 * cannot see, which then renders the access-denied panel instead of the content.
 * Silently redrawing a forbidden deep link as a permitted tab would hide the
 * refusal. Only an unrecognised value falls back, and it falls back to the first
 * tab this role can actually see so the page is never blank.
 */
const resolveActiveKey = (requested, visibleTabs) =>
  findTab(requested)?.key ?? visibleTabs[0]?.key ?? TABS[0].key;

const DataImportsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission, isLoading } = useAuth();

  const visibleTabs = useMemo(
    () => TABS.filter(({ permission }) => hasPermission(permission)),
    [hasPermission]
  );

  const activeKey = resolveActiveKey(searchParams.get('tab'), visibleTabs);
  const activeTab = findTab(activeKey) ?? TABS[0];
  const { Component } = activeTab;

  // The tab lives in the same searchParams as any child filter, so it survives a reload.
  const handleTabChange = useCallback(
    (key) => {
      const params = new URLSearchParams(searchParams);
      params.set('tab', key);
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  return (
    <div className={styles.page}>
      <span className={styles.eyebrow}>Data Imports</span>

      {/* Hidden while auth resolves: an empty strip would otherwise flash, then
          repaint with tabs, reading as a permission change that never happened. */}
      {!isLoading && (
        <div className={styles.tabs} role="tablist" aria-label="Data imports">
          {visibleTabs.map(({ key, label }) => (
            <button
              key={key}
              id={`data-imports-tab-${key}`}
              type="button"
              role="tab"
              aria-selected={activeKey === key}
              aria-controls={`data-imports-panel-${key}`}
              className={`${styles.tab} ${activeKey === key ? styles.tabActive : ''}`}
              onClick={() => handleTabChange(key)}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      <div
        id={`data-imports-panel-${activeKey}`}
        role="tabpanel"
        aria-labelledby={`data-imports-tab-${activeKey}`}
      >
        <PermissionRoute permission={activeTab.permission}>
          <Component />
        </PermissionRoute>
      </div>
    </div>
  );
};

export default DataImportsPage;
